import { Injectable, OnDestroy } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, throwError } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { differenceInCalendarDays } from 'date-fns';

import { Lang2, ToursSearch } from 'package-types';

import { ToursSearchEndpoint } from '../api/tours-search.endpoint';
import { SocketIOService } from '../socket-io.service';
import { Search } from '@classes/search/search';
import { SearchCollection } from '@classes/search/search-collection';
import { Interval } from '@classes/search/interval';
import { GlobalSearchService } from './global-search.service';
import { IrrelevantSearchError } from './errors/irrelevant-search.error';
import { PlatformService } from '../platform.service';

import { SearchParams } from '@interfaces/modules/tours/search';
import { ToursSearchResult } from '@interfaces/tours-search-result';
import { defaultToursSearchSort, ToursSearchSort } from '@interfaces/services/api/tours-search.endpoint';

@UntilDestroy()
@Injectable()
export class ToursSearchService implements OnDestroy {
  private readonly searchCollection = new SearchCollection();
  private readonly searchFallback = new Interval(() => this.searchCollection.forEach(search => this.getResult(search)));
  private readonly pendingResultRequests = new Set<string>();

  constructor(
    private globalSearch: GlobalSearchService,
    private toursSearch: ToursSearchEndpoint,
    private socket: SocketIOService,
    private translate: TranslateService,
    private platform: PlatformService
  ) {
    this.init();
  }

  init(): void {
    if (this.platform.isServer()) {
      return;
    }
    this.onSearchResultsPageReady();
    this.initSearchFallback();
    this.searchCollection.destroySearch$
      .pipe(untilDestroyed(this))
      .subscribe((id) => {
        this.socket.leave(id);
        if (this.searchCollection.isEmpty()) {
          this.searchFallback.stop();
        }
      });
  }

  search(params: SearchParams, id?: string, page: number = 1, sort: ToursSearchSort = defaultToursSearchSort): Observable<ToursSearchResult> {
    if (this.platform.isServer()) {
      return of();
    }
    if (this.isSearchIrrelevant(params)) {
      return throwError(new IrrelevantSearchError(params));
    }

    if (id) {
      const search = this.searchCollection.get(id, page);
      if (search) {
        return search.asObservable();
      }

      return this.toursSearch.result(id, page, this.translate.currentLang as Lang2, sort)
        .pipe(
          switchMap((response) => {
            if (response.status === ToursSearch.ResultStatus.Ready || response.status === ToursSearch.ResultStatus.Failed) {
              return of({ page, params, id, sort, res: response });
            }
            const search = this.add({ page, params, id, sort, res: response });

            return search.asObservable();
          })
        );
    }

    return this.toursSearch.search(params, this.translate.currentLang as Lang2, page, sort)
      .pipe(
        switchMap(({ searchId, result, status }) => {
          if (status === ToursSearch.ResultStatus.Ready) {
            return of({ page, params, id: searchId, sort, res: { result, status } });
          }
          const search = this.add({ page, params, id: searchId, sort, res: { result, status } });

          return search.asObservable();
        })
      );
  }

  finishActiveSearches(): void {
    this.searchCollection.destroy();
    this.searchFallback.stop();
    this.pendingResultRequests.clear();
  }

  ngOnDestroy(): void {
    this.searchCollection.destroy();
    this.searchFallback.stop();
    this.pendingResultRequests.clear();
  }

  private isSearchIrrelevant({ dates }: SearchParams): boolean {
    return Boolean(
      dates?.checkIn
      && differenceInCalendarDays(new Date(dates.checkIn), new Date()) < 1
    );
  }

  private add(resultState: ToursSearchResult): Search {
    const search = Search.from(resultState);
    this.searchCollection.set(search);
    this.socket.join(search.getSearchId());
    this.searchFallback.start();

    return search;
  }

  private onSearchResultsPageReady() {
    this.socket.searchResultStatus$
      .pipe(untilDestroyed(this))
      .subscribe((payload) => {
        const { searchId, status } = payload;
        const page = (payload as any).page ?? 1;
        const search = this.searchCollection.get(searchId, page);
        if (search) {
          if (
            status === ToursSearch.ResultStatus.Ready
            || status === ToursSearch.ResultStatus.Partial
            || status === ToursSearch.ResultStatus.InProgress
          ) {
            this.getResult(search, page);
          } else if (status === ToursSearch.ResultStatus.Failed) {
            this.searchCollection.consumeResult(searchId, page, { status, result: { hotels: [], totalCount: 0 } });
          }
        } else if (!this.searchCollection.has(searchId)) {
          this.socket.leave(searchId);
        }
      });
  }

  private initSearchFallback() {
    this.socket.disconnect$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.searchFallback.start());

    this.socket.connect$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.searchCollection.forEach((search) => {
          this.socket.join(search.getSearchId());
          this.getResult(search);
        });
      });
  }

  private getResult(search: Search, page: number = search.getPageNumber()): void {
    const sort = search.getSort();
    const key = `${search.getSearchId()}:${page}:${sort ?? defaultToursSearchSort}`;
    if (this.pendingResultRequests.has(key)) {
      return;
    }
    this.pendingResultRequests.add(key);
    this.toursSearch.result(search.getSearchId(), page, this.translate.currentLang as Lang2, sort)
      .pipe(
        finalize(() => this.pendingResultRequests.delete(key)),
        untilDestroyed(this)
      )
      .subscribe(response => this.searchCollection.consumeResult(search.getSearchId(), page, response));
  }
}
